import { deleteDataAPI, postDataAPI } from './api'
import { parseLocalDate, toLocalDateKey } from './dateUtils'

/**
 * Normalize a loose date string to YYYY-MM-DD using local-time parsing.
 * Mirrors the `normalizeLooseDate` logic in ShowsPage so the AI auto-import
 * produces the same canonical dates as manual-add and CSV import.
 */
function normalizeLooseDate(raw: string): string | undefined {
  const value = raw.trim()
  if (!value) return undefined

  const token =
    value.match(/^(\d{1,4}[./-]\d{1,2}[./-]\d{1,4})/)?.[1] ??
    value.match(/(\d{1,4}[./-]\d{1,2}[./-]\d{1,4})/)?.[1] ??
    value

  const patterns: Array<{ re: RegExp; kind: 'mdy' | 'dmy' | 'ymd' }> = [
    { re: /^(\d{1,2})\.(\d{1,2})\.(\d{2,4})$/, kind: 'mdy' },
    { re: /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/, kind: 'mdy' },
    { re: /^(\d{1,2})-(\d{1,2})-(\d{2,4})$/, kind: 'dmy' },
    { re: /^(\d{4})-(\d{1,2})-(\d{1,2})$/, kind: 'ymd' },
    { re: /^(\d{4})\/(\d{1,2})\/(\d{1,2})$/, kind: 'ymd' },
    { re: /^(\d{4})\.(\d{1,2})\.(\d{1,2})$/, kind: 'ymd' },
  ]

  for (const p of patterns) {
    const m = token.match(p.re)
    if (!m) continue

    let year = ''
    let month = ''
    let day = ''

    if (p.kind === 'ymd') {
      year = m[1]; month = m[2]; day = m[3]
    } else if (p.kind === 'mdy') {
      month = m[1]; day = m[2]; year = m[3]
    } else {
      day = m[1]; month = m[2]; year = m[3]
    }

    const yearNumRaw = Number(year)
    const yearNum = year.length === 2 ? 2000 + yearNumRaw : yearNumRaw
    const mmNum = Number(month)
    const ddNum = Number(day)
    const yy = String(yearNum)
    const mm = String(mmNum).padStart(2, '0')
    const dd = String(ddNum).padStart(2, '0')

    const d = new Date(yearNum, mmNum - 1, ddNum, 12, 0, 0, 0)
    if (d.getFullYear() === yearNum && d.getMonth() === mmNum - 1 && d.getDate() === ddNum) {
      return `${yy}-${mm}-${dd}`
    }
  }

  const cleaned = token.replace(/^(mon|tue|tues|wed|thu|thur|thurs|fri|sat|sun)\s*,?\s*/i, '')
  const parsed = new Date(cleaned)
  if (!Number.isNaN(parsed.getTime())) {
    const yy = parsed.getFullYear()
    const mm = String(parsed.getMonth() + 1).padStart(2, '0')
    const dd = String(parsed.getDate()).padStart(2, '0')
    return `${yy}-${mm}-${dd}`
  }

  return undefined
}

/**
 * Safely convert any date-ish value to YYYY-MM-DD using local-time parsing.
 * Falls through multiple strategies: normalizeLooseDate → parseLocalDate → toLocalDateKey.
 */
function safeLocalDateKey(raw: unknown): string {
  if (raw === null || raw === undefined) return toLocalDateKey(new Date())
  const str = String(raw).trim()
  if (!str) return toLocalDateKey(new Date())

  // Try the loose-date normalizer first (handles CSV / human formats)
  const loose = normalizeLooseDate(str)
  if (loose) return loose

  // Fall back to parseLocalDate (handles ISO timestamps without off-by-one)
  const parsed = parseLocalDate(str)
  if (parsed) return toLocalDateKey(parsed)

  return toLocalDateKey(new Date())
}

export async function importBandsintownViaAI(artistName: string) {
  if (!artistName || !artistName.trim()) {
    return { success: false, savedCount: 0, error: 'Artist name is required' }
  }

  const prompt = `Find shows for ${artistName} on Bandsintown and add them to my bookings app with status confirmed (so they appear in the confirmed tab), including the correct city and state/province for each show from Bandsintown, and avoiding duplicate bookings if they already exist.`

  const searchResponse = await postDataAPI('user/chatWithAIForDizee', {
    prompt,
    conversation: [],
    conversationId: '',
  })

  const functionResults = searchResponse?.data?.actions_taken
  const conversationId = searchResponse?.data?.conversationId

  if (!Array.isArray(functionResults)) {
    return { success: false, savedCount: 0, error: 'No show data extracted from search' }
  }

  const webSearchResult = functionResults.find((a: any) => a?.type === 'search' && Array.isArray(a?.shows))
  const shows = webSearchResult?.shows || []

  if (!shows.length) {
    return { success: true, savedCount: 0, message: 'No upcoming shows found on Bandsintown' }
  }

  const bookings = shows.map((show: any) => ({
    artist: artistName,
    perf_date: safeLocalDateKey(show.date),
    venue: show.venue,
    city: show.city || '',
    state: show.state || '',
    country: show.country || '',
    status: 'confirmed',
    ticket_link: show.ticketLink || '',
    source: 'bandsintown',
    bandsintown_id: show.ticketLink ? String(show.ticketLink).split('/').pop()?.split('?')[0] : '',
  }))

  // Clean up conversation so it doesn't interfere with other AI prompts (legacy behavior)
  if (conversationId) {
    try {
      await deleteDataAPI(`user/conversations/${conversationId}`)
    } catch {
      // non-critical
    }
  }

  // The AI prompt already saved these shows to the backend, so don't store
  // them as pending for the approval modal — that would cause a double-import
  // and trigger duplicate detection.
  return { success: true, savedCount: bookings.length, bookings }
}

